import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signInWithLoginWidget, type TelegramLoginUser } from '../hooks/useTelegramContext';
import { mocksEnabled } from '../mocks/browser';
import { ME } from '../mocks/fixtures';

const BOT_USERNAME = import.meta.env.VITE_BOT_USERNAME ?? '';

declare global {
  interface Window {
    onTelegramAuth?: (user: TelegramLoginUser) => void;
  }
}

/**
 * Browser sign-in. The Telegram Login Widget posts a signed payload back
 * through a global callback; we stash it and every request then carries
 * `X-Telegram-Login-Data`. Nothing is verified client-side — `bot/api/auth.py`
 * checks the HMAC.
 *
 * BotFather `/setdomain` must point at the hosting domain or the widget
 * silently refuses to render — that is the usual cause of a blank slot here.
 */
export function AuthScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') ?? '/';
  const slot = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.onTelegramAuth = (user) => {
      signInWithLoginWidget(user);
      navigate(next, { replace: true });
    };
    return () => {
      delete window.onTelegramAuth;
    };
  }, [navigate, next]);

  useEffect(() => {
    if (!BOT_USERNAME || !slot.current || slot.current.childElementCount > 0) return;
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.setAttribute('data-telegram-login', BOT_USERNAME);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-radius', '22');
    script.setAttribute('data-onauth', 'onTelegramAuth(user)');
    script.setAttribute('data-request-access', 'write');
    slot.current.appendChild(script);
  }, []);

  return (
    <div className="centered-screen centered-screen--auth">
      <div className="auth__mark">✈</div>
      <div className="auth__title">Manager · Lists</div>
      <div className="auth__body">
        Sign in with Telegram to see your lists on the web. Same account, same data.
      </div>

      <div ref={slot} style={{ marginTop: 26 }} />

      {mocksEnabled ? (
        <button
          type="button"
          className="auth__button"
          onClick={() => {
            // Mock-only shortcut so every screen is reachable without a
            // backend. The `hash` is nonsense on purpose: against the real API
            // this payload fails `bot/api/auth.py`'s HMAC check, which is
            // exactly what should happen if mocks are ever left on by mistake.
            const parts = (ME.name ?? '').split(' ');
            signInWithLoginWidget({
              id: ME.id,
              first_name: parts[0] || 'Mock',
              last_name: parts.slice(1).join(' '),
              username: ME.username ?? undefined,
              auth_date: Math.floor(Date.now() / 1000),
              hash: 'mock-session-not-verified',
            });
            navigate(next, { replace: true });
          }}
        >
          <span style={{ fontSize: 18 }}>✈</span> Continue with the mock account
        </button>
      ) : null}

      <div className="auth__hint">Telegram Login Widget</div>
    </div>
  );
}
